import { Link } from 'react-router-dom';
import { 
  Shield, Award, Users, Target, Heart, Leaf, 
  CheckCircle, ArrowRight, Phone, Clock, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function About() {
  const values = [
    {
      icon: Shield,
      title: 'Safety First',
      description: 'We prioritize the safety of your family, pets, and the environment in every treatment we provide.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for excellence in every service, ensuring thorough and effective pest elimination.'
    },
    {
      icon: Heart,
      title: 'Customer Care',
      description: 'Your satisfaction is our top priority. We treat every home as if it were our own.'
    },
    {
      icon: Leaf,
      title: 'Eco-Conscious',
      description: 'We use environmentally responsible methods and products to protect our planet.'
    }
  ];

  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '15K+', label: 'Happy Customers' },
    { number: '50+', label: 'Expert Technicians' },
    { number: '100%', label: 'Satisfaction Rate' }
  ];

  const team = [
    { name: 'Licensed Technicians', desc: 'State-certified professionals with extensive training' },
    { name: 'Background Checked', desc: 'All staff undergo thorough background screening' },
    { name: 'Ongoing Education', desc: 'Regular training on latest pest control methods' },
    { name: 'Friendly Service', desc: 'Courteous and respectful in your home or business' }
  ];

  return (
    <div className="page-transition pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="about-hero">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2E7D32] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                About Us
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Trusted Partner in Pest Control Since 2010
              </h1>
              <p className="text-lg text-[#52525B] mb-8 leading-relaxed">
                For over 15 years, Primes Pick has been protecting homes and businesses across 
                Minneapolis and the surrounding areas. We have built our reputation on trust, 
                expertise, and a commitment to delivering exceptional results every time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary w-full sm:w-auto" data-testid="about-contact-btn">
                    Get Free Inspection
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+18883568891" className="btn-accent w-full sm:w-auto text-center">
                  <Phone className="w-5 h-5" />
                  Call Us
                </a>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=500&fit=crop"
                alt="Primes Pick team"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#2E7D32]" data-testid="about-stats">
        <div className="container-main">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl sm:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stat.number}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding" data-testid="about-story">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Our Story
              </h2>
            </div>
            <div className="prose prose-lg max-w-none text-[#52525B]">
              <p className="mb-6">
                Primes Pick was founded in 2010 with a simple mission: to provide families and businesses 
                with safe, effective, and affordable pest control solutions. What started as a small 
                family-owned operation has grown into one of the most trusted pest control companies 
                in the Minneapolis area.
              </p>
              <p className="mb-6">
                Our founder recognized the need for a pest control service that prioritized customer 
                safety and environmental responsibility. This vision has guided every decision we have 
                made, from the products we use to the technicians we hire.
              </p>
              <p>
                Today, we continue to uphold these values while embracing new technologies and methods 
                that make our services even more effective. Our team of over 50 licensed technicians 
                serves thousands of customers each year, and we are proud of the relationships we have 
                built with our community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section-padding bg-[#F7F9F7]" data-testid="about-mission">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Our Mission & Values
            </h2>
            <p className="text-lg text-[#52525B] max-w-2xl mx-auto">
              We are driven by a commitment to excellence and a passion for protecting what matters most to you.
            </p>
          </div>
          
          {/* Mission Statement */}
          <div className="bg-white rounded-2xl p-8 sm:p-12 shadow-lg mb-12 max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-[#2E7D32] rounded-xl flex items-center justify-center">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A1C1A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Our Mission</h3>
            </div>
            <p className="text-lg text-[#52525B] leading-relaxed">
              To provide exceptional pest control services that protect the health, safety, and comfort 
              of families and businesses, while maintaining the highest standards of environmental 
              responsibility and customer care.
            </p>
          </div>

          {/* Values Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg" data-testid={`value-card-${index}`}>
                <div className="w-12 h-12 bg-[#E8F5E9] rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-[#2E7D32]" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1C1A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-sm text-[#52525B]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="section-padding" data-testid="about-team">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=600&h=500&fit=crop"
                alt="Professional pest control technician"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Licensed & Trained Technicians
              </h2>
              <p className="text-lg text-[#52525B] mb-8 leading-relaxed">
                Our team consists of highly trained professionals who are passionate about pest control. 
                Every technician is state-licensed, background-checked, and committed to providing 
                the best possible service.
              </p>
              <div className="space-y-4">
                {team.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-[#1A1C1A]">{item.name}</h3>
                      <p className="text-sm text-[#52525B]">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Satisfaction */}
      <section className="section-padding bg-[#E8F5E9]" data-testid="about-satisfaction">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-8 h-8 text-[#F9A825] fill-[#F9A825]" />
              ))}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A1C1A] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              100% Customer Satisfaction Guarantee
            </h2>
            <p className="text-lg text-[#52525B] mb-8 leading-relaxed">
              We stand behind our work with a complete satisfaction guarantee. If pests return between 
              scheduled treatments, so do we — at no additional cost. Your peace of mind is our priority.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white px-6 py-3 rounded-full shadow-sm">
                <span className="text-[#2E7D32] font-semibold">Free Re-treatments</span>
              </div>
              <div className="bg-white px-6 py-3 rounded-full shadow-sm">
                <span className="text-[#2E7D32] font-semibold">Money-Back Guarantee</span>
              </div>
              <div className="bg-white px-6 py-3 rounded-full shadow-sm">
                <span className="text-[#2E7D32] font-semibold">Flexible Scheduling</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-16 sm:py-20" data-testid="about-cta">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Experience the Primes Pick Difference?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust us to keep their homes and businesses pest-free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-white text-[#2E7D32] hover:bg-white/90 rounded-full px-8 py-4 font-semibold shadow-lg">
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+18883568891" className="btn-accent">
              <Phone className="w-5 h-5" />
              Call: +1-888-356-8891
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
